import {useEffect, useState} from 'react';
import {AlertCircle, Save} from 'lucide-react';
import {
  Alert,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  Spinner
} from '@kroll-resolver-crisp/ui-components-react';
import {getApiErrorMessage} from '@/api/client';
import {useAnalystSearch, useModerationSettings, useUpdateModerationSettings} from '@/api/hooks';
import {type UpdateModerationSettingsPayload} from '@/api/moderationSettings';
import {type Analyst} from '@/api/shares';

const DEFAULTS: UpdateModerationSettingsPayload = {
  channel_review_audience: 'off',
  channel_review_analysts: [],
  lease_seconds: 300,
  heartbeat_seconds: 30,
  skip_minutes: 60,
  batch_sizes: [5, 10, 20],
  queues_enabled: false
};

export function ModerationSettingsCard() {
  const {data} = useModerationSettings();
  const {data: analysts = []} = useAnalystSearch();
  const update = useUpdateModerationSettings();
  const [form, setForm] = useState(DEFAULTS);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setForm({
        channel_review_audience: data.channel_review_audience,
        channel_review_analysts: data.channel_review_analysts,
        lease_seconds: data.lease_seconds,
        heartbeat_seconds: data.heartbeat_seconds,
        skip_minutes: data.skip_minutes,
        batch_sizes: data.batch_sizes,
        queues_enabled: data.queues_enabled
      });
    }
  }, [data]);

  async function save() {
    setError(null);
    try {
      await update.mutateAsync({
        ...form,
        channel_review_analysts:
          form.channel_review_audience === 'selected' ? form.channel_review_analysts : []
      });
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to save moderation settings.'));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Moderation queue</CardTitle>
        <CardDescription>Configure moderation work claiming and batching.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Field>
          <FieldLabel htmlFor="moderation-channel-review">Channel review audience</FieldLabel>
          <FieldDescription>Choose which analysts receive channel review work.</FieldDescription>
          <NativeSelect
            id="moderation-channel-review"
            className="w-full"
            value={form.channel_review_audience}
            onChange={(event) => {
              setForm((current) => ({
                ...current,
                channel_review_audience: event.target
                  .value as UpdateModerationSettingsPayload['channel_review_audience']
              }));
            }}>
            <NativeSelectOption value="off">Off</NativeSelectOption>
            <NativeSelectOption value="selected">Selected analysts</NativeSelectOption>
            <NativeSelectOption value="everyone">Everyone</NativeSelectOption>
          </NativeSelect>
        </Field>
        {form.channel_review_audience === 'selected' ? (
          <fieldset className="border-border space-y-2 border p-3">
            <legend className="px-1 text-xs font-medium">Channel review analysts</legend>
            {mergeAnalysts(analysts, data?.channel_review_analyst_details ?? []).map((analyst) => (
              <div key={analyst.email} className="flex items-center gap-2">
                <Checkbox
                  aria-label={`${analyst.display_name} (${analyst.email})`}
                  checked={form.channel_review_analysts.includes(analyst.auth0_subject)}
                  onCheckedChange={(checked) => {
                    setForm((current) => ({
                      ...current,
                      channel_review_analysts: checked
                        ? [...current.channel_review_analysts, analyst.auth0_subject]
                        : current.channel_review_analysts.filter(
                            (subject) => subject !== analyst.auth0_subject
                          )
                    }));
                  }}
                />
                <span>
                  {analyst.display_name} ({analyst.email})
                </span>
              </div>
            ))}
          </fieldset>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <NumberField
            id="moderation-lease-seconds"
            label="Lease duration"
            help="How long a claimed task remains owned before it can be reclaimed."
            value={form.lease_seconds}
            onChange={(value) => {
              setForm((current) => ({...current, lease_seconds: value}));
            }}
          />
          <NumberField
            id="moderation-heartbeat-seconds"
            label="Heartbeat interval"
            help="How often workers renew their claimed task lease."
            value={form.heartbeat_seconds}
            onChange={(value) => {
              setForm((current) => ({...current, heartbeat_seconds: value}));
            }}
          />
          <NumberField
            id="moderation-skip-minutes"
            label="Skip duration"
            help="How long a skipped task stays out of the queue."
            value={form.skip_minutes}
            onChange={(value) => {
              setForm((current) => ({...current, skip_minutes: value}));
            }}
          />
          <div className="space-y-2 sm:col-span-2">
            <p className="text-xs font-medium">Batch sizes</p>
            <p className="text-muted-foreground text-xs">
              Exactly three ascending batch sizes between 2 and 20.
            </p>
            <div className="grid gap-4 sm:grid-cols-3">
              {form.batch_sizes.map((size, index) => (
                <NumberField
                  key={size}
                  id={`moderation-batch-size-${index + 1}`}
                  label={`Batch ${index + 1}`}
                  help="Number of tasks served at once."
                  value={size}
                  onChange={(value) => {
                    setForm((current) => ({
                      ...current,
                      batch_sizes: current.batch_sizes.map((currentSize, currentIndex) =>
                        currentIndex === index ? value : currentSize
                      )
                    }));
                  }}
                />
              ))}
            </div>
          </div>
          <Field>
            <div className="flex items-center gap-2">
              <Checkbox
                id="moderation-dev-queues"
                checked={form.queues_enabled}
                onCheckedChange={(checked) => {
                  setForm((current) => ({...current, queues_enabled: Boolean(checked)}));
                }}
              />
              <FieldLabel htmlFor="moderation-dev-queues">Enable queues</FieldLabel>
            </div>
            <FieldDescription>
              Allow permitted analysts to access queue work in this environment.
            </FieldDescription>
          </Field>
        </div>
        {error ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertTitle>{error}</AlertTitle>
          </Alert>
        ) : null}
      </CardContent>
      <CardFooter className="justify-between gap-2">
        {data ? (
          <p className="text-muted-foreground text-xs">
            Last updated by {data.updated_by ?? 'unknown'} at{' '}
            {data.updated_at ? new Date(data.updated_at).toLocaleString() : 'unknown'}.
          </p>
        ) : (
          <span />
        )}
        <Button disabled={update.isPending} type="button" onClick={save}>
          {update.isPending ? <Spinner /> : <Save />}
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
      </CardFooter>
    </Card>
  );
}

function mergeAnalysts(analysts: Analyst[], details: Analyst[]) {
  const merged = new Map(analysts.map((analyst) => [analyst.email, analyst]));
  for (const analyst of details) {
    merged.set(analyst.email, analyst);
  }

  return [...merged.values()];
}

function NumberField({
  id,
  label,
  help,
  value,
  onChange
}: {
  readonly id: string;
  readonly label: string;
  readonly help: string;
  readonly value: number;
  readonly onChange: (value: number) => void;
}) {
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input
        id={id}
        type="number"
        value={value}
        onChange={(event) => {
          onChange(Number(event.target.value));
        }}
      />
      <FieldDescription>{help}</FieldDescription>
    </Field>
  );
}
